import { doc, setDoc, getDoc, updateDoc, deleteField } from "firebase/firestore";
import { db } from "@/firebase";

const buildShoppingListDocId = (userId) => `${userId}-shopping_list`;

const normalizeListName = (name) => {
  const base = String(name || "liste")
    .trim()
    .toLowerCase()
    .replace(/\s+/g, "_")
    .replace(/[^a-z0-9_]/g, "_")
    .replace(/_+/g, "_")
    .replace(/^_+|_+$/g, "");
  return base || "liste";
};

export const createShoppingListId = (name, now = Date.now()) =>
  `${now}-${normalizeListName(name)}`;

/**
 * Récupère toutes les listes d'un utilisateur.
 * Stockage: collection "ShoppingLists", doc "${userId}-shopping_list"
 * Retourne une map { [listId]: list }
 */
export async function getUserShoppingLists(userId) {
  if (!userId) return {};
  const docRef = doc(db, "ShoppingLists", buildShoppingListDocId(userId));
  try {
    const snap = await getDoc(docRef);
    if (!snap.exists()) return {};
    return snap.data() || {};
  } catch (e) {
    console.error("❌ Erreur getUserShoppingLists:", e);
    throw e;
  }
}

/**
 * Ajoute une liste pour un utilisateur.
 * listData doit contenir au minimum { name, items }
 * Retourne { id }
 */
export async function addShoppingList(userId, listData = {}) {
  if (!userId) throw new Error("userId manquant");
  const listName = listData.name || "Liste";
  const listId = createShoppingListId(listName);
  const docRef = doc(db, "ShoppingLists", buildShoppingListDocId(userId));

  const payload = {
    ...listData,
    id: listId,
    name: listName,
    items: Array.isArray(listData.items) ? listData.items : [],
    createdAt: Date.now(),
    updatedAt: Date.now(),
  };

  try {
    await setDoc(docRef, { [listId]: payload }, { merge: true });
    return { id: listId };
  } catch (e) {
    console.error("❌ Erreur addShoppingList:", e);
    throw e;
  }
}

/**
 * Met à jour une liste existante (ou la crée si absente).
 * listData écrase la liste au complet pour listId.
 */
export async function updateShoppingList(userId, listId, listData = {}) {
  if (!userId || !listId) throw new Error("userId ou listId manquant");
  const docRef = doc(db, "ShoppingLists", buildShoppingListDocId(userId));
  let createdAt = listData.createdAt;
  if (!createdAt) {
    try {
      const snap = await getDoc(docRef);
      if (snap.exists()) {
        const existing = snap.data()?.[listId];
        createdAt = existing?.createdAt;
      }
    } catch (e) {
      console.warn("⚠️ Impossible de récupérer createdAt existant:", e);
    }
  }

  const payload = {
    ...listData,
    id: listId,
    createdAt: createdAt ?? Date.now(),
    updatedAt: Date.now(),
  };
  try {
    await setDoc(docRef, { [listId]: payload }, { merge: true });
    return { id: listId };
  } catch (e) {
    console.error("❌ Erreur updateShoppingList:", e);
    throw e;
  }
}

/**
 * Supprime une liste d'un utilisateur.
 */
export async function deleteShoppingList(userId, listId) {
  if (!userId || !listId) throw new Error("userId ou listId manquant");
  const docRef = doc(db, "ShoppingLists", buildShoppingListDocId(userId));
  try {
    await updateDoc(docRef, { [listId]: deleteField() });
    return { deleted: true };
  } catch (e) {
    if (e.code === "not-found" || /No document to update/.test(String(e))) {
      return { deleted: false };
    }
    console.error("❌ Erreur deleteShoppingList:", e);
    throw e;
  }
}

/**
 * Récupère une liste par son id.
 * Retourne { id, ...list } ou null.
 */
export async function getUserShoppingListById(userId, listId) {
  if (!userId || !listId) return null;
  const docRef = doc(db, "ShoppingLists", buildShoppingListDocId(userId));
  try {
    const snap = await getDoc(docRef);
    if (!snap.exists()) return null;
    const data = snap.data() || {};
    const list = data[listId] || null;
    return list ? { id: listId, ...list } : null;
  } catch (e) {
    console.error("❌ Erreur getUserShoppingListById:", e);
    throw e;
  }
}
